import { useCallback, useMemo, useState } from 'react';

import { useCarousel } from '@rocco/components/carousel/carousel';

import { useIslandSection } from './use-island-section';
import type { IslandMode } from './types';

interface IslandCarouselOptions {
    /** Accessible label for the pagination, e.g. "Project gallery". */
    label: string;
    /** Default 1 — slightly above link/cta so pagination sticks while peeking. */
    priority?: number;
    enabled?: boolean;
    /** Autoplay delay (ms) — the duration of the active dot's progress fill. Default 4000. */
    autoplayDelayMs?: number;
}

/**
 * Convenience bridge for carousels: reads the enclosing `<Carousel>`'s embla
 * state and registers a live `pagination` island mode. Must be called inside a
 * `<Carousel>`. Returns a ref for the element whose vertical extent should drive
 * in-view (attach it to an element spanning the carousel).
 *
 * Autoplay is driven by the island's progress fill (it advances on completion),
 * so pause/resume stays in sync — there's no separate embla autoplay timer.
 *
 * Note: a section that already owns the embla api (e.g. via `setApi`) can build
 * the `pagination` mode itself and call {@link useIslandSection} directly — that
 * lets the ref sit on the full-height `<section>`.
 */
export const useIslandCarousel = <T extends HTMLElement = HTMLElement>(
    options: IslandCarouselOptions,
) => {
    const { api, currentSlide, totalSlides } = useCarousel();
    const [isPlaying, setIsPlaying] = useState(true);
    const togglePlay = useCallback(
        () => setIsPlaying((playing) => !playing),
        [],
    );
    const advance = useCallback(() => api?.scrollNext(), [api]);
    const mode = useMemo<IslandMode>(
        () => ({
            kind: 'pagination',
            label: options.label,
            total: totalSlides,
            activeIndex: currentSlide,
            onSelect: (index) => api?.scrollTo(index),
            isPlaying,
            onTogglePlay: togglePlay,
            onAdvance: advance,
            autoplayDelayMs: options.autoplayDelayMs ?? 4000,
        }),
        [
            api,
            currentSlide,
            totalSlides,
            options.label,
            options.autoplayDelayMs,
            isPlaying,
            togglePlay,
            advance,
        ],
    );
    return useIslandSection<T>(mode, {
        priority: options.priority ?? 1,
        enabled: options.enabled,
    });
};
